/**
 * 保存領域から、トロフィー判定に使う「全国の進み具合」を集める。
 * トロフィーの獲得記録（いつ取ったか・新着か）もここで持つ。
 */

import { categoriesOf, FOOD_CATEGORIES, type FoodCategoryId } from '../game/config/foodCategories';
import { metaStorageKey, stageStorageKey } from '../game/config/gameConfig';
import { idMigrationFor } from '../game/config/idMigrations';
import { areaOf } from '../game/config/regions';
import { STAGES } from '../game/config/stages';
import { StorageSystem, type StorageLike } from '../game/systems/StorageSystem';
import { DailyBook } from './daily';
import { JsonStore } from './jsonStore';
import { evaluateTrophies, type MetaProgress, type PrefProgress, type TrophyStatus } from './trophies';

/** 制覇率の対象になるステージ（draft とご当地ステージは除く）。 */
function targetStages() {
  return Object.entries(STAGES).filter(([, stage]) => !stage.draft && !stage.parentId);
}

export function collectMetaProgress(storage?: StorageLike | null): MetaProgress {
  const prefs: PrefProgress[] = [];
  const categories = new Map<FoodCategoryId, { total: number; found: number }>(
    FOOD_CATEGORIES.map((c) => [c.id, { total: 0, found: 0 }]),
  );

  for (const [id, stage] of targetStages()) {
    const save = new StorageSystem({ key: stageStorageKey(id), storage, idMigration: idMigrationFor(id) });
    const unlocked = new Set(save.unlockedItemIds);
    const tips = stage.scenery?.tour?.scenes.flatMap((s) => s.hotspots.map((h) => h.id)) ?? [];
    const found = save.discoveredTipIds.filter((tipId) => tips.includes(tipId)).length;

    prefs.push({
      regionId: stage.regionId,
      regionName: stage.regionName,
      areaId: areaOf(stage.regionId)?.id ?? null,
      maxLevel: stage.items.length,
      reached: save.maxLevelReached,
      reachedNoAssist: save.bestLevelNoAssist,
      unlockedItemIds: save.unlockedItemIds.filter((itemId) =>
        stage.items.some((item) => item.id === itemId),
      ),
      tipsFound: found,
      tipsTotal: tips.length,
      playCount: save.playCount,
    });

    for (const item of stage.items) {
      for (const categoryId of categoriesOf(stage.regionId, item.id)) {
        const stat = categories.get(categoryId);
        if (!stat) continue;
        stat.total += 1;
        if (unlocked.has(item.id)) stat.found += 1;
      }
    }
  }

  const daily = new DailyBook(storage).view();
  return {
    prefs,
    categories,
    daily: { bestStreak: daily.bestStreak, totalDays: daily.totalDays },
  };
}

/* ------------------------------- 獲得記録 ------------------------------- */

interface TrophyRecord {
  /** トロフィーID -> 獲得した日時（ISO）。 */
  unlocked: Record<string, string>;
  /** 棚でまだ見ていない新着。 */
  unseen: string[];
}

function normalizeRecord(raw: unknown): TrophyRecord {
  const r = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const unlocked: Record<string, string> = {};
  if (r.unlocked && typeof r.unlocked === 'object') {
    for (const [k, v] of Object.entries(r.unlocked as Record<string, unknown>)) {
      if (typeof v === 'string') unlocked[k] = v;
    }
  }
  const unseen = Array.isArray(r.unseen) ? r.unseen.filter((x): x is string => typeof x === 'string') : [];
  return { unlocked, unseen };
}

export interface TrophySnapshot {
  statuses: TrophyStatus[];
  progress: MetaProgress;
  /** 今回はじめて獲得したもの。 */
  newlyUnlocked: TrophyStatus[];
  /** 獲得日時。 */
  unlockedAt: Readonly<Record<string, string>>;
  unseen: ReadonlySet<string>;
}

export class TrophyBook {
  private readonly store: JsonStore<TrophyRecord>;
  private readonly storage: StorageLike | null | undefined;

  constructor(storage?: StorageLike | null) {
    this.storage = storage;
    this.store = new JsonStore(metaStorageKey('trophies'), normalizeRecord, storage);
  }

  /**
   * いまの保存領域から判定し、新しく取れたものを記録する。
   *
   * 一度取ったトロフィーは、あとから条件を満たさなくなっても（県が増えて分母が変わっても）消さない。
   */
  sync(now: Date = new Date()): TrophySnapshot {
    const progress = collectMetaProgress(this.storage);
    const statuses = evaluateTrophies(progress);
    const record = this.store.data;
    const newly = statuses.filter((s) => s.achieved && !record.unlocked[s.trophy.id]);
    if (newly.length > 0) {
      this.store.update((d) => {
        for (const s of newly) {
          d.unlocked[s.trophy.id] = now.toISOString();
          if (!d.unseen.includes(s.trophy.id)) d.unseen.push(s.trophy.id);
        }
      });
    }
    // 過去に取ったものは achieved 扱いに揃える
    const merged = statuses.map((s) =>
      this.store.data.unlocked[s.trophy.id] ? { ...s, achieved: true, current: s.target } : s,
    );
    return {
      statuses: merged,
      progress,
      newlyUnlocked: newly,
      unlockedAt: { ...this.store.data.unlocked },
      unseen: new Set(this.store.data.unseen),
    };
  }

  markAllSeen(): void {
    if (this.store.data.unseen.length === 0) return;
    this.store.update((d) => {
      d.unseen = [];
    });
  }

  get unseenCount(): number {
    return this.store.data.unseen.length;
  }
}
